import { from, Observable, of } from 'rxjs';
import { ofType } from 'redux-observable';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { CreateErrorNotice, CreateSuccessNotice } from '../../store/actions/toaster.action';
import OrganizationService from '../../shared/services/organization.service';
import UserService from '../../technicians/services/user.service';
import { UserRoles } from '../../shared/models/user';
import {
  InternalSignUpAction,
  InternalSignUpActions,
  InternalSignUpFailed,
  InternalSignUpRequest,
  InternalSignUpSuccess,
} from '../actions/internalSignUp.action';
import { ServerErrorMessages } from '../../shared/utils/server-error-utils';
import { CountryCode } from '../../shared/models/register';

const getRefIdByCountry = (country: CountryCode) => {
  switch(country) {
    case CountryCode.EU: 
      return process.env.ORGANIZATION_SIGNUP_REFERRER_ID_S1;  
    case CountryCode.restOfWorld: 
      return process.env.ORGANIZATION_SIGNUP_REFERRER_ID_S0_REST;  
    case CountryCode.US:
    default:
      return process.env.ORGANIZATION_SIGNUP_REFERRER_ID;  
  }
};

const internalSignUpEpic = (action$: Observable<InternalSignUpAction>) =>
  action$.pipe(
    ofType<InternalSignUpRequest>(InternalSignUpActions.InternalSignUpRequest),
    switchMap((action) => {
      const { payload } = action;
      const parent_id = -1;
      const referrerOrgId = getRefIdByCountry(payload.shard as CountryCode);
      
      return from(
        OrganizationService.createOrg(
          parent_id,
          payload.orgName,
          payload.adminEmail,
          ['no-billing'],
          {
            industry: payload.info.industry,
            num_employees: payload.info.num_employees,
            referrer: payload.info.referrer,
            country: payload.shard,
          },
          referrerOrgId ? parseInt(referrerOrgId, 10) : undefined
        )
      ).pipe(
        switchMap((org) =>
          from(
            UserService.createUser(
              org._id,
              {
                name: `${payload.adminFirstName} ${payload.adminLastName}`,
                email: payload.adminEmail,
                phone: payload.adminPhone,
                password: payload.password,
                roles: [UserRoles.Admin],
              },
              false
            )
          ).pipe(
            switchMap((user) =>
              from(
                UserService.updateUserRoles(
                  user.id,
                  user.orgId,
                  [UserRoles.Admin],
                  false
                )
              ).pipe(
                mergeMap(() =>
                  of(
                    new InternalSignUpSuccess(),
                    new CreateSuccessNotice(
                      'Success',
                      'Organization and user created successfully'
                    )
                  )
                )
              )
            )
          )
        ),
        catchError((error: Error) =>
          of(
            new InternalSignUpFailed(
              error.message || 'Failed to complete internal sign up'
            ),
            new CreateErrorNotice(
              'Error',
              ServerErrorMessages[error.message as keyof typeof ServerErrorMessages] ||
                'An error occurred during registration'
            )
          )
        )
      );
    })
  );

const InternalSignUpEpics = [internalSignUpEpic];

export default InternalSignUpEpics;
